import { getFestivePrice } from '../utils/festivePrice';

export interface MenProduct {
  id: string;
  name: string;
  category: 'jacket' | 'kurta' | 'pants' | 'shirts' | 'tshirts';
  price: number;         // = sellingPrice (GST-inclusive final price)
  originalPrice: number; // crossed-out MRP
  discountPercent: number;
  amountSaved: number;
  description: string;
  imageUrl: string;
  availableSizes: string[];
  availableColors: string[];
}

// Helper function to generate description based on name and category
const generateDescription = (name: string, category: string): string => {
  const categoryDescriptions: Record<string, string> = {
    'jacket': 'Stylish jacket perfect for various occasions and weather conditions',
    'kurta': 'Traditional and contemporary kurta for comfortable ethnic wear',
    'pants': 'Comfortable and stylish pants for everyday wear',
    'shirts': 'Elegant shirts for formal and casual occasions',
    'tshirts': 'Comfortable t-shirts for casual everyday wear'
  };
  
  const baseDesc = categoryDescriptions[category] || 'Quality fashion item';
  
  // Add some keywords based on the name
  if (name.toLowerCase().includes('formal')) return `Formal ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('casual')) return `Casual ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('party')) return `Party wear ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('designer')) return `Designer ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('traditional')) return `Traditional ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('summer')) return `Summer ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('winter')) return `Winter ${baseDesc.toLowerCase()}`;
  
  return baseDesc;
};

// Helper function to generate sizes based on category
const generateSizes = (category: string): string[] => {
  if (category === 'kurta') {
    return ['S', 'M', 'L', 'XL', 'XXL'];
  }
  return ['S', 'M', 'L', 'XL'];
};

// Helper function to generate colors based on category and index
const generateColors = (category: string, index: number): string[] => {
  const colorPools: Record<string, string[][]> = {
    'jacket': [
      ['Black', 'Brown', 'Navy'],
      ['Gray', 'Green', 'Blue'],
      ['Brown', 'Black', 'Olive'],
      ['Navy', 'Gray', 'Burgundy'],
      ['Black', 'Gray', 'Blue']
    ],
    'kurta': [
      ['White', 'Blue', 'Beige'],
      ['Black', 'Maroon', 'Gold'],
      ['Blue', 'Green', 'Brown'],
      ['Cream', 'Gray', 'Navy'],
      ['White', 'Red', 'Gold']
    ],
    'pants': [
      ['Black', 'Gray', 'Navy'],
      ['Brown', 'Beige', 'Olive'],
      ['Blue', 'Gray', 'Black'],
      ['Khaki', 'Brown', 'Green'],
      ['Black', 'Navy', 'Charcoal']
    ],
    'shirts': [
      ['White', 'Blue', 'Pink'],
      ['Black', 'Gray', 'Navy'],
      ['Blue', 'Green', 'Purple'],
      ['White', 'Check', 'Striped'],
      ['Pastel Blue', 'Pastel Pink', 'Pastel Green']
    ],
    'tshirts': [
      ['White', 'Black', 'Gray'],
      ['Blue', 'Red', 'Green'],
      ['Black', 'White', 'Navy'],
      ['Gray', 'Olive', 'Brown'],
      ['Multicolor', 'Printed', 'Striped']
    ]
  };
  
  const pool = colorPools[category] || [['Blue', 'Black', 'Gray']];
  return pool[index % pool.length];
};

// Image data for each category
const categoryImages: Record<string, string[]> = {
  'jacket': [
    'Blazer Jacket (Casual).jpg',
    'Corduroy Jacket.jpg',
    'Cropped Jacket.jpg',
    'Fleece Jacket.jpg',
    'Hooded Jacket.jpg',
    'Leather Biker Jacket.jpg',
    'nehru_jacket.jpg',
    'Parka Jacket.jpg',
    'Puffer Jacket.jpg',
    'Quilted Jacket.jpg',
    'quilted_bomber_jacket.jpg',
    'Sherpa Lined Jacket.jpg',
    'Track Jacket.jpg',
    'Trench Coat Jacket.jpg',
    'Utility Jacket.jpg',
    'Varsity Jacket.jpg',
    'Windbreaker Jacket.jpg'
  ],
  'kurta': [
    'Angrakha Style Kurta (2).jpg',
    'Asymmetric Designer Kurta.jpg',
    'Bandhani Print Kurta.jpg',
    'Chikankari Kurta.jpg',
    'Collar Neck Kurta.jpg',
    'cotton_kurta_pajama.jpg',
    'Denim Kurta.jpg',
    'Digital Print Kurta.jpg',
    'emboridered_kurta.jpg',
    'Embroidered Festive Kurta.jpg',
    'Ethnic_Waistcoat.jpg',
    'High-Low Hem Kurta (2).jpg',
    'Hooded_Sweatshirt.jpg',
    'Jacket_Style_Kurta_Set.jpg',
    'Layered Kurta Set.jpg',
    'Linen Summer Kurta.jpg',
    'Mirror Work Kurta.jpg',
    'Nehru Jacket Kurta Set.jpg',
    'Pathani Kurta Set (2).jpg',
    'Pathani_Kurta_Set.jpg',
    'Printed Casual Kurta.jpg',
    'Printed_Linen_Blazer.jpg',
    'Short Kurta.jpg',
    'Silk Blend Wedding Kurta.jpg',
    'Solid Cotton Straight Kurta.jpg',
    'Velvet Festive Kurta.jpg',
    'Zipper Kurta.jpg'
  ],
  'pants': [
    'Cargo_Pants.jpg',
    'Casual Chino Trousers.jpg',
    'Checkered Formal Trousers.jpg',
    'Corduroy Trousers.jpg',
    'Cropped Ankle Trousers.jpg',
    'Denim Trousers.jpg',
    'Drawstring Waist Trousers.jpg',
    'formal_trousers.jpg',
    'High Waist Trousers.jpg',
    'Jogger Style Trousers.jpg',
    'Linen Trousers.jpg',
    'Pleated Trousers.jpg',
    'Printed Casual Trousers.jpg',
    'Relaxed Fit Trousers.jpg',
    'Side Stripe Trousers.jpg',
    'Stretchable Comfort Trousers.jpg',
    'Tapered Fit Trousers.jpg',
    'Wool Blend Trousers.jpg'
  ],
  'shirts': [
    'Asymmetrical Designer Shirt.jpg',
    'Chambray Shirt.jpg',
    'Checked_Casual_Shirt.jpg',
    'Corduroy Shirt.jpg',
    'Cuban Collar Shirt.jpg',
    'Denim Shirt.jpg',
    'Flannel Shirt.jpg',
    'Formal White Shirt.jpg',
    'Half Sleeve Resort Shirt.jpg',
    'Linen Summer Shirt.jpg',
    'linen_mandrain_shirt.jpg',
    'Mandarin Collar Shirt.jpg',
    'Oversized Street Style Shirt.jpg',
    'Oxford Button-Down Shirt.jpg',
    'Printed Vacation Shirt.jpg',
    'printed_casual_shirt.webp',
    'Satin Party Shirt.jpg',
    'Slim Fit Casual Shirt.jpg',
    'Striped Formal Shirt.jpg',
    'Tie-Dye Shirt.jpg',
    'Utility Pocket Shirt.jpg',
    'Velvet Party Shirt.jpg'
  ],
  'tshirts': [
    'Acid Wash T-Shirt.jpg',
    'Camouflage Print T-Shirt.jpg',
    'Color Block T-Shirt.jpg',
    'Graphic Printed T-Shirt.jpg',
    'Henley Neck T-Shirt.jpg',
    'Linen Blend Summer T-Shirt.jpg',
    'Longline Extended Length T-Shirt.jpg',
    'Oversized_T-Shirt.png',
    'Plain Cotton Crew Neck T-Shirt.jpg',
    'Pocket Detail T-Shirt.jpg',
    'polo_t-shirt.jpg',
    'Raglan Sleeve T-Shirt.jpg',
    'round_neck_t-shirt.jpg',
    'Sports Performance T-Shirt.jpg',
    'Striped Casual T-Shirt.jpg',
    'striped_oxford_tshirt.jpg',
    'Tie-Dye Printed T-Shirt.jpg',
    'Typography Quote T-Shirt.jpg',
    'V-Neck Slim Fit T-Shirt.jpg',
    'Washed Vintage T-Shirt.jpg'
  ]
};

// Map category keys to actual folder names on disk (case-sensitive for S3)
const categoryFolderMap: Record<string, string> = {
  'jacket': 'jacket',
  'kurta': 'kurta',
  'pants': 'Pants',
  'shirts': 'Shirts',
  'tshirts': 'Tshirts'
};

// Generate product data for ALL images
const generateAllMenProducts = (): MenProduct[] => {
  const products: MenProduct[] = [];
  let productId = 1;

  Object.entries(categoryImages).forEach(([category, images]) => {
    images.forEach((imageName, index) => {
      // Clean up the name for display
      const cleanName = imageName
        .replace(/\.[^/.]+$/, '') // Remove extension
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/\([^)]*\)/g, '') // Remove parentheses content
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim()
        .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letters
      
      const fp = getFestivePrice(productId);
      products.push({
        id: `m${productId++}`,
        name: cleanName,
        category: category as 'jacket' | 'kurta' | 'pants' | 'shirts' | 'tshirts',
        price: fp.sellingPrice,
        originalPrice: fp.originalPrice,
        discountPercent: fp.discountPercent,
        amountSaved: fp.amountSaved,
        description: generateDescription(cleanName, category),
        imageUrl: `/assets/images/mens/${categoryFolderMap[category] || category}/${imageName}`,
        availableSizes: generateSizes(category),
        availableColors: generateColors(category, index)
      });
    });
  });

  return products;
};

// Generate ALL men's products
export const mensProducts: MenProduct[] = generateAllMenProducts();

// Get all products
export const getAllMenProducts = (): MenProduct[] => {
  return mensProducts;
};

// Get products by category
export const getMenProductsByCategory = (category: string): MenProduct[] => {
  if (category === 'all') {
    return mensProducts;
  }
  return mensProducts.filter(product => product.category === category);
};

// Get product by ID
export const getMenProductById = (id: string): MenProduct | undefined => {
  return mensProducts.find(product => product.id === id);
};

// Get all categories
export const getAllMenCategories = (): string[] => {
  const categories = mensProducts.map(product => product.category);
  return ['all', ...Array.from(new Set(categories))];
};

// Get category display name
export const getMenCategoryDisplayName = (category: string): string => {
  const displayNames: Record<string, string> = {
    'all': 'All Products',
    'jacket': 'Jackets',
    'kurta': 'Kurtas',
    'pants': 'Pants',
    'shirts': 'Shirts',
    'tshirts': 'T-Shirts'
  };
  return displayNames[category] || category;
};

// Get products count by category
export const getMenProductsCountByCategory = (): Record<string, number> => {
  const counts: Record<string, number> = {
    'all': mensProducts.length
  };
  
  mensProducts.forEach(product => {
    counts[product.category] = (counts[product.category] || 0) + 1;
  });
  
  return counts;
};